import { useEffect, useState } from "react";
import type { DownloadInfo } from "@handvero/shared";
import { api, errorText } from "../api";
import { chooseFolder, disconnectFolder, inWindowsApp, openFolder, syncNow, useSyncStatus } from "./pcSync";
import { Message } from "./ui";

function time(iso: string) {
  return new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}

function plural(n: number, one: string, many: string) {
  return `${n} ${n === 1 ? one : many}`;
}

/** Kurzer Stand für die Seitenleiste, nur wenn ein Ordner gewählt ist: läuft, Fehler oder zuletzt abgeglichen. */
export function PcSyncBadge() {
  const s = useSyncStatus();
  if (!s.available || !s.folder) return null;
  const text = s.running ? "Ordner wird abgeglichen …" : s.error ? "Ordnerabgleich gestört" : s.lastRun ? `Ordner abgeglichen ${time(s.lastRun)}` : "";
  return text ? <small className={`dsync-badge${s.error ? " bad" : ""}`}>{text}</small> : null;
}

function DownloadHint() {
  const [info, setInfo] = useState<DownloadInfo | null>(null);
  useEffect(() => {
    api.get<DownloadInfo>("/api/downloads").then(setInfo, () => undefined);
  }, []);
  return (
    <>
      <p className="muted-s">
        In der Windows-App können Sie zusätzlich einen Ordner auf Ihrem PC wählen. HandVero legt dort alle Projekte mit Ordnern und Dateien ab und
        hält sie aktuell.
      </p>
      {info?.windows && (
        <a className="app-btn small" href="/api/downloads/windows" download={info.fileName}>
          HandVero für Windows herunterladen
        </a>
      )}
    </>
  );
}

/** Speicherort in den Einstellungen: nur in HandVero oder zusätzlich in einem Ordner auf diesem PC. */
export function PcFolderSetting() {
  const s = useSyncStatus();
  const [error, setError] = useState("");
  const [details, setDetails] = useState(false);

  async function act(fn: () => Promise<void>) {
    setError("");
    try {
      await fn();
    } catch (err) {
      setError(errorText(err));
    }
  }

  const app = inWindowsApp();
  const sum = s.summary;
  const changes = sum
    ? [
        sum.down && `${plural(sum.down, "Datei", "Dateien")} geholt`,
        sum.up && `${plural(sum.up, "Datei", "Dateien")} hochgeladen`,
        sum.moved && `${plural(sum.moved, "Datei", "Dateien")} umbenannt`,
        sum.folders && `${plural(sum.folders, "Ordner", "Ordner")} angelegt`,
        sum.trashed && `${plural(sum.trashed, "Datei", "Dateien")} in den Papierkorb`,
      ].filter(Boolean)
    : [];

  return (
    <fieldset className="choice pcf">
      <legend>Speicherort</legend>
      <div>
        <button type="button" className={!s.folder ? "on" : undefined} aria-pressed={!s.folder} disabled={!app} onClick={() => void act(disconnectFolder)}>
          Nur in HandVero
        </button>
        <button type="button" className={s.folder ? "on" : undefined} aria-pressed={!!s.folder} disabled={!app || s.running} onClick={() => void act(chooseFolder)}>
          {s.folder ? "Anderen Ordner wählen" : "Auch in einem Ordner am PC"}
        </button>
      </div>
      {!app && <DownloadHint />}
      {app && !s.folder && (
        <p className="muted-s">Alle Projekte liegen sicher in HandVero. Wählen Sie zusätzlich einen Ordner, sehen Sie sie auch im Explorer.</p>
      )}
      {app && s.folder && (
        <div className="pcf-box">
          <b className="pcf-path">{s.folder}</b>
          <small className={s.error ? "pcf-bad" : undefined}>
            {s.running
              ? (s.activity ?? "Gleicht ab …")
              : s.error
                ? s.error
                : s.lastRun
                  ? `Abgeglichen um ${time(s.lastRun)} Uhr. ${changes.length ? `${changes.join(", ")}.` : "Alles aktuell."}`
                  : "Noch nicht abgeglichen."}
          </small>
          <div className="pcf-actions">
            <button type="button" className="app-btn small" onClick={() => void act(() => openFolder())}>
              Im Explorer öffnen
            </button>
            <button type="button" className="app-btn small ghost" disabled={s.running} onClick={() => void act(syncNow)}>
              Jetzt abgleichen
            </button>
            <button type="button" className="btn-text" onClick={() => setDetails(!details)}>
              {details ? "Weniger" : "So funktioniert es"}
            </button>
          </div>
          {s.problems.length > 0 && (
            <details className="pcf-problems">
              <summary>{plural(s.problems.length, "Datei wurde", "Dateien wurden")} nicht abgeglichen</summary>
              <ul>
                {s.problems.slice(0, 30).map((p, i) => (
                  <li key={`${p.path}-${i}`}>
                    <b>{p.path || "Abgleich"}</b> {p.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {details && (
            <ul className="pcf-rules">
              <li>Für jedes Projekt gibt es einen Ordner mit denselben Unterordnern wie in HandVero.</li>
              <li>Neues aus HandVero kommt jede Minute dazu, auch Fotos vom Handy.</li>
              <li>Was Sie in einen Unterordner eines Projekts legen, landet in HandVero. Neue Unterordner werden mit angelegt.</li>
              <li>Eine am PC bearbeitete Datei ersetzt die alte Fassung in HandVero.</li>
              <li>Löschen bitte in HandVero. Gelöschtes legt der Abgleich im versteckten Ordner „.handvero/Papierkorb“ ab.</li>
              <li>„Nur in HandVero“ beendet den Abgleich. Die Dateien bleiben im Ordner liegen.</li>
            </ul>
          )}
        </div>
      )}
      <Message>{error}</Message>
    </fieldset>
  );
}
